import { createContext, useContext, useState, type ReactNode } from 'react';

import ToastMessage from '@/components/molecules/ToastMessage';
import type { ToastPosition, ToastProps } from '@/components/molecules/ToastMessage/types';

type ToastContextType = {
  showToast: (title: string, description: string, type?: ToastProps['type'], position?: ToastPosition) => void;
  isVisible?: boolean;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const initialProps = {
    id: 'toast-message',
    isVisible: false,
    onClose: () => null,
  };

  const [toast, setToast] = useState<ToastProps>(initialProps);

  const showToast = (
    title: string,
    description: string,
    type: ToastProps['type'] = 'info',
    position: ToastPosition = 'topRight',
    duration = 3000
  ) => {
    setToast({
      description,
      duration,
      id: title,
      isVisible: true,
      onClose: () => setToast(initialProps),
      title,
      type,
      position,
    });
  };

  return (
    <ToastContext.Provider value={{ showToast, isVisible: toast?.isVisible }}>
      {children}
      <ToastMessage key={toast.id} {...toast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
